import { createApi } from '@reduxjs/toolkit/query/react';
import { IAttribute, IAzureContact, IExternalContactResult } from '../../models/agent';
import { createRbacBaseQuery, post, createDirectoryBaseQuery, del  } from '../../utils/helpers';

const agentApi = createApi({
  reducerPath: 'agent',
  tagTypes: ['WrapUpTimer', 'AzureContact', 'AttributeKeys', 'AgentAttributes', 'DefaultWorkItemThresholds', 'AgentWorkItemThresholds', 'BreakNames', 'Workflows'],
  baseQuery: createRbacBaseQuery(''),
  endpoints: (builder) => ({
    searchExternalAccounts: builder.query<IAzureContact[], string>({
      queryFn: async (searchTerm, api, extraOptions) => {
        const query = createDirectoryBaseQuery('/ExternalContacts');
        const result = await query(`?term=${searchTerm}&ProviderNo=0&Top=10`, api, extraOptions);

        if (result.data)
          return { data: (result.data as IExternalContactResult).results, meta: result.meta };
        else if (result.error)
          return { error: result.error, meta: result.meta };

        return { error: 'Unknown error' };
      },
      providesTags: (results) => results?.map(c => ({ type: 'AzureContact', id: c.id })) ?? [],
    }),
    getExternalAccountById: builder.query<IAzureContact, string>({
      queryFn: async (contactId, api) => {
        const query = createDirectoryBaseQuery('/ExternalContacts');
        const result = await query(contactId, api, {});

        if (result.data)
          return { data: result.data as IAzureContact, meta: result.meta };
        else if (result.error)
          return { error: result.error, meta: result.meta };

        return { error: 'Unknown error' };
      },
      providesTags: (_, __, contactId) => [{ type: 'AzureContact', id: contactId }],
    }),
    getTenantAttributeKeys: builder.query<string[], void>({
      query: () => '/attributes',
      providesTags: ['AttributeKeys'],
    }),
    getAgentAttributes: builder.query<IAttribute[], string>({
      query: (agentId) => `/resource/${agentId}/attributes`,
      providesTags: (_, __, agentId) => [{ type: 'AgentAttributes', id: agentId }],
    }),
    updateAgentAttributes: builder.mutation<void, { agentId: string, attributes: IAttribute[] }>({
      query: ({ agentId, attributes }) => post(`/resource/${agentId}/attributes`, attributes),
      invalidatesTags: (_, __, { agentId }) => ['AttributeKeys', { type: 'AgentAttributes', id: agentId }],
    }),
    deleteAgentAttributes: builder.mutation<void, string>({
      query: (agentId) => del(`/resource/${agentId}/attributes`),
      invalidatesTags: (_, __, agentId) => ['AttributeKeys', { type: 'AgentAttributes', id: agentId }],
    }),
  }),
});

export default agentApi;

export const {
  useSearchExternalAccountsQuery,
  useGetExternalAccountByIdQuery,
  useGetTenantAttributeKeysQuery,
  useGetAgentAttributesQuery,
  useUpdateAgentAttributesMutation,
  useDeleteAgentAttributesMutation,
} = agentApi;